import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { backendProvider } from '../../../services/backend'
import { showErrorToast } from '../../../utils/toast'
import { getUserFriendlyError } from '../../../utils/errorMessages'
import BrainiacsLoader from '../../../components/atoms/BrainiacsLoader'

// Search params type for the callback
interface CallbackSearch {
  userId?: string
  secret?: string
  type?: string
  teamId?: string
  membershipId?: string
}

export const Route = createFileRoute('/auth/callback')({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => {
    return {
      userId: search.userId as string | undefined,
      secret: search.secret as string | undefined,
      type: search.type as string | undefined,
      teamId: search.teamId as string | undefined,
      membershipId: search.membershipId as string | undefined,
    }
  },
  component: AuthCallbackPage,
})

function AuthCallbackPage() {
  const navigate = useNavigate()
  const { userId, secret, type, teamId, membershipId } = Route.useSearch()
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const hasProcessedCallbackRef = useRef(false)
  const redirectTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    if (hasProcessedCallbackRef.current) {
      return
    }

    hasProcessedCallbackRef.current = true

    const handleCallback = async () => {
      console.log('🔐 Auth callback page loaded')
      console.log('   userId:', userId)
      console.log('   secret:', secret ? secret.substring(0, 10) + '...' : 'MISSING')
      console.log('   type:', type)

      // Determine if this is a Magic Link, Team Invite, or OAuth callback
      const isMagicLink = type === 'magic-link'
      const isTeamInviteLogin = type === 'team-invite-login'
      const isTeamInvite = type === 'team-invite'

      if ((isMagicLink || isTeamInviteLogin || isTeamInvite) && (!userId || !secret)) {
        console.error('❌ Missing userId or secret in callback')
        setStatus('error')
        setErrorMessage('Missing authentication parameters. Please try again.')
        return
      }

      if (isTeamInviteLogin) {
        console.log('👥 Processing team invite login (magic link)...')
        try {
          // The invite email embeds a users.createToken secret — treat it as a magic link.
          // The team membership is already confirmed server-side when the invite was sent.
          await backendProvider.auth.verifyMagicLink(userId, secret)
          console.log('✅ Team invite login completed successfully')
          setStatus('success')
          redirectTimeoutRef.current = window.setTimeout(() => navigate({ to: '/' }), 500)
        } catch (error: any) {
          console.error('❌ Team invite login error:', error)
          const friendlyError = getUserFriendlyError(error)
          setStatus('error')
          setErrorMessage(friendlyError.message)
          showErrorToast(friendlyError.message)
        }
      } else if (isMagicLink) {
        console.log('🔗 Processing Magic Link callback...')
        try {
          await backendProvider.auth.verifyMagicLink(userId, secret)
          console.log('✅ Magic Link authentication completed successfully')
          setStatus('success')
          // Navigate to home after successful auth
          redirectTimeoutRef.current = window.setTimeout(() => navigate({ to: '/' }), 500)
        } catch (error: any) {
          console.error('❌ Magic Link verification error:', error)
          const friendlyError = getUserFriendlyError(error)
          setStatus('error')
          setErrorMessage(friendlyError.message)
          showErrorToast(friendlyError.message)
        }
      } else if (isTeamInvite) {
        console.log('👥 Processing team invitation callback...')

        if (!teamId || !membershipId) {
          setStatus('error')
          setErrorMessage('Missing team invitation parameters. Please use the original invitation email link.')
          return
        }

        try {
          await backendProvider.auth.acceptTeamInvitation(teamId, membershipId, userId, secret)
          setStatus('success')
          redirectTimeoutRef.current = window.setTimeout(() => navigate({ to: '/' }), 800)
        } catch (error: any) {
          console.error('❌ Team invitation callback error:', error)
          const friendlyError = getUserFriendlyError(error)
          setStatus('error')
          setErrorMessage(friendlyError.message)
          showErrorToast(friendlyError.message)
        }
      } else {
        console.log('🔐 Processing OAuth callback...')
        try {
          if (!userId || !secret) {
            // Appwrite may have used session flow (creates session server-side
            // during OAuth redirect without returning userId/secret as URL params).
            // Check if a session was already established before showing error.
            console.log('🔄 No URL params — checking if session flow was used...')
            await backendProvider.auth.waitForInitialization()
            const existingUser = backendProvider.auth.getCurrentUser()
            if (existingUser) {
              console.log('✅ Session already established via session flow, redirecting...')
              localStorage.removeItem('oauth_provider')
              setStatus('success')
              redirectTimeoutRef.current = window.setTimeout(() => navigate({ to: '/' }), 500)
              return
            }

            console.error('❌ Missing userId or secret in OAuth callback and no session found')
            setStatus('error')
            setErrorMessage('Missing authentication parameters. Please try again.')
            return
          }

          // Get provider from localStorage (stored before OAuth redirect)
          const provider = localStorage.getItem('oauth_provider') as 'google' | 'linkedin' | null
          const finalProvider = provider || 'google'

          console.log(`🔄 Processing ${finalProvider} OAuth callback...`)
          await backendProvider.auth.handleOAuthCallback(userId, secret, finalProvider)

          console.log('✅ OAuth authentication completed successfully')

          // Clean up localStorage
          localStorage.removeItem('oauth_provider')

          setStatus('success')
          // Navigate to home after successful auth
          redirectTimeoutRef.current = window.setTimeout(() => navigate({ to: '/' }), 500)
        } catch (error: any) {
          console.error('❌ OAuth callback error:', error)
          const friendlyError = getUserFriendlyError(error)
          setStatus('error')
          setErrorMessage(friendlyError.message)
          showErrorToast(friendlyError.message)
        }
      }
    }

    handleCallback()

    return () => {
      if (redirectTimeoutRef.current !== null) {
        window.clearTimeout(redirectTimeoutRef.current)
      }
    }
  }, [membershipId, navigate, secret, teamId, type, userId])

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: '#050505',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'HarmonyOS Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
        {status === 'processing' && (
          <BrainiacsLoader
            variant="section"
            message={type === 'team-invite' || type === 'team-invite-login' ? 'Accepting team invitation...' : 'Completing sign in...'}
            subtitle={
              type === 'team-invite' || type === 'team-invite-login'
                ? 'Please wait while we confirm your access to the team'
                : 'Please wait while we verify your credentials'
            }
            className="py-0"
          />
        )}

        {status === 'success' && (
          <>
            <div
              style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                borderRadius: '50%',
                margin: '0 auto 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p style={{ color: '#fff', fontSize: '16px', marginBottom: '8px' }}>
              {type === 'team-invite' || type === 'team-invite-login' ? 'Invitation accepted!' : 'Sign in successful!'}
            </p>
            <p style={{ color: '#666', fontSize: '14px' }}>
              {type === 'team-invite' || type === 'team-invite-login'
                ? 'Redirecting you to the workspace...'
                : 'Redirecting you to the dashboard...'}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div
              style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                borderRadius: '50%',
                margin: '0 auto 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p style={{ color: '#fff', fontSize: '16px', marginBottom: '8px' }}>
              Sign in failed
            </p>
            <p style={{ color: '#EF4444', fontSize: '14px', marginBottom: '16px' }}>
              {errorMessage || 'An error occurred during authentication'}
            </p>
            <button
              onClick={() => navigate({ to: '/' })}
              style={{
                background: 'linear-gradient(135deg, #5260FF 0%, #3A45B0 100%)',
                color: '#fff',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Return to Login
            </button>
          </>
        )}
      </div>
    </div>
  )
}
